import { useCloseContextMenuOnScroll } from '@hooks/useCloseContextMenuOnScroll';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { RequestStatus } from 'features/request/requestSlice';
import {
    ChapterEnum,
    type ConstructionTypeData,
    type BimcCalculationLimitedCostData,
    type UercCalculationSeasonData,
} from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { Button, ContextMenu, IconKebab } from 'uikit';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import * as R from 'ramda';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBimDeleteCalculationLimitedCost } from '../hooks/useBimDeleteCalculationLimitedCost';

import { BimCalculationLimitedEditModal } from './BimCalculationLimitedEditModal';

const getItemLabel = R.prop('label');

interface IBimCalculationLimitedRowMenu {
    row?: BimcCalculationLimitedCostData;
    onOperationSuccess?: (id?: number) => void;
    constructionTypes: ConstructionTypeData[];
    calculationId: number;
    constructionTypeId: number;
    costRounding: number;
    isCalculationCompleted: boolean;
    seasons?: UercCalculationSeasonData[] | null;
}

const deleteHeader = 'УДАЛЕНИЕ ЛИМИТИРОВАННЫХ ЗАТРАТ';

export const BimCalculationLimitedRowMenu: React.FC<IBimCalculationLimitedRowMenu> = (props) => {
    const { row, onOperationSuccess, calculationId, isCalculationCompleted, constructionTypes, constructionTypeId } =
        props;
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const ref = useRef(null);
    const { calculationLimited: calculationLimitedRules } = useAccess();

    useCloseContextMenuOnScroll({ setOpen: setIsOpen });

    const handleOnClick = useCallback(() => {
        setIsOpen(R.not);
    }, []);

    const { makeRequest, reset, data, status, error } = useBimDeleteCalculationLimitedCost({
        calculationId,
        calculationLimitedCostId: row?.id,
    });

    useEffect(() => {
        if (data?.result && status === RequestStatus.SUCCESS) {
            onOperationSuccess?.(row?.id);
            setIsConfirmationDialogOpen(false);
            reset();
        }
    }, [data?.result, row?.id, onOperationSuccess, reset, status]);

    const canEdit = useMemo(
        () => !isCalculationCompleted && calculationLimitedRules.canEdit,
        [calculationLimitedRules.canEdit, isCalculationCompleted],
    );

    const items = useMemo(() => {
        const canDelete = !isCalculationCompleted && calculationLimitedRules?.canDelete;

        return [
            ...(canEdit
                ? [
                      {
                          label: 'Редактировать',
                          action: () => {
                              setIsEditDialogOpen(true);
                          },
                      },
                      {
                          label: 'Удалить',
                          action: () => {
                              setIsConfirmationDialogOpen(true);
                          },
                          disabled: !canDelete,
                      },
                  ]
                : [
                      {
                          label: 'Просмотреть',
                          action: () => {
                              setIsEditDialogOpen(true);
                          },
                          disabled: !calculationLimitedRules?.canRead,
                      },
                  ]),
        ];
    }, [isCalculationCompleted, calculationLimitedRules?.canDelete, calculationLimitedRules?.canRead, canEdit]);

    const onItemOnClick = useCallback(
        (item) => () => {
            item.action();
            setIsOpen(false);
        },
        [],
    );

    const onCloseDeleteDialog = useCallback(() => {
        setIsConfirmationDialogOpen(false);
    }, []);

    const onDeleteConfirmed = useCallback(() => {
        makeRequest();
        setIsSubmitted(true);
    }, [makeRequest]);

    const onCloseModalError = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const onCreateReportDownload = useCallback(() => {
        ExportCSVReport(data?.validation);
    }, [data?.validation]);

    const onEditModalClose = useCallback(
        (isSuccess) => {
            setIsEditDialogOpen(false);

            if (isSuccess) {
                onOperationSuccess?.();
            }
        },
        [onOperationSuccess],
    );

    return (
        <div
            style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <Button
                iconLeft={IconKebab}
                onlyIcon
                view="clear"
                size="xs"
                style={{ marginTop: 12, marginBottom: 12, marginLeft: 4 }}
                onClick={handleOnClick}
                ref={ref}
            />

            <ContextMenu
                isOpen={isOpen}
                items={items}
                getItemLabel={getItemLabel}
                anchorRef={ref}
                direction="downStartLeft"
                getItemOnClick={onItemOnClick}
                onClickOutside={handleOnClick}
            />
            <ConfirmationDialog
                textHeader={deleteHeader}
                textConfirmation="Вы уверены, что хотите удалить запись?"
                cancelButtonText="Не удалять"
                confirmButtonText="Удалить"
                isOpened={isConfirmationDialogOpen}
                onSubmit={onDeleteConfirmed}
                onClose={onCloseDeleteDialog}
            />
            <ErrorHandlingModal
                titleText={deleteHeader}
                isSubmitted={isSubmitted}
                statusRequest={status}
                dataRequest={data}
                errorsRequest={error}
                handleCloseModalError={onCloseModalError}
                handleCreateReport={onCreateReportDownload}
            />
            {isEditDialogOpen && (
                <BimCalculationLimitedEditModal
                    handleClose={onEditModalClose}
                    calculationId={calculationId}
                    constructionTypeId={constructionTypeId}
                    constructionTypes={constructionTypes}
                    limitedIdProp={row?.id}
                    isEditModal={canEdit}
                    isAdditionalTransportProp={row?.chapter === ChapterEnum.Chapter1_7}
                />
            )}
        </div>
    );
};

BimCalculationLimitedRowMenu.displayName = 'BimCalculationLimitedRowMenu';
